import { useEffect, useRef } from 'react'
import { ConvergenceAuthMethod } from '../../model'
import { useJourneyId } from '../../state/cashAuth/hooks'
import { CASH_AUTH_SIGNALS, USER_JOURNEY_NAMES, USER_JOURNEY_TIMEOUT_MS } from './constants'
import { getCashAuthJourneyTags } from './cashAuthTags'
import { getCheckoutJourneyTags, type CheckoutJourneyTagValues } from './checkoutTags'
import { clearJourneyMarker, markJourneyMarkerHandoff, readJourneyMarker } from './journeyMarker'
import { JourneyOutcome, JourneyTerminationReason } from './types'
import { getExpiredJourneyTimeoutTerminationReason, getReplacedJourneyCompletion } from './timeout'
import { useUserJourneyContext } from './UserJourneyContext'
import { useCheckoutJourneyOwnership } from './MobileUserJourneyOwnership'

let grantHandoffPending = false

export const beginCashGrantHandoff = (checkoutToken: string): void => {
  grantHandoffPending = true
  markJourneyMarkerHandoff(checkoutToken)
  window.addEventListener(
    'pageshow',
    (event) => {
      if (event.persisted) {
        grantHandoffPending = false
      }
    },
    { once: true }
  )
}

export const isCashGrantHandoffPending = (): boolean => grantHandoffPending

export const resetCashGrantHandoffForTesting = (): void => {
  grantHandoffPending = false
}

export interface CashAuthJourneyAnchorProps {
  isAuthorized: boolean
  isDeclined: boolean
  hasError: boolean
  isPollingLimitExceeded: boolean
  isRedirectingToGrant?: boolean
  featureFlagsReady: boolean
  cashAuthMethod?: ConvergenceAuthMethod
  checkoutTags?: CheckoutJourneyTagValues
}

export const useCashAuthJourneyAnchors = ({
  isAuthorized,
  isDeclined,
  hasError,
  isPollingLimitExceeded,
  isRedirectingToGrant = false,
  cashAuthMethod,
  checkoutTags = {},
}: CashAuthJourneyAnchorProps): boolean => {
  const { active, addFriction, addTag, completeJourney, resumeJourney, startJourney } = useUserJourneyContext()
  const { claimRocketshipOwnership, journeyId, owner, ownershipExitReady, reportingEligibilityResolved, reportingEnabled, waitForOwnershipResolution } =
    useCheckoutJourneyOwnership()
  const cashAuthJourneyId = useJourneyId()
  const resumed = useRef(false)

  const journeyActive = active?.name === USER_JOURNEY_NAMES.CHECKOUT

  useEffect(() => {
    if (reportingEligibilityResolved && !ownershipExitReady && (isAuthorized || isDeclined || hasError || isPollingLimitExceeded || isRedirectingToGrant)) {
      void waitForOwnershipResolution()
    }
  }, [
    reportingEligibilityResolved,
    ownershipExitReady,
    isAuthorized,
    isDeclined,
    hasError,
    isPollingLimitExceeded,
    isRedirectingToGrant,
    waitForOwnershipResolution,
  ])

  useEffect(() => {
    if (!reportingEligibilityResolved || resumed.current) {
      return
    }
    const leavingCashAuth = isAuthorized || isDeclined || hasError || isPollingLimitExceeded
    const marker = readJourneyMarker()
    const matchingHandoff =
      marker?.handoff === true &&
      (marker.checkoutToken === undefined || marker.checkoutToken === checkoutTags.initial?.token) &&
      marker.journey.name === USER_JOURNEY_NAMES.CHECKOUT
    if (!reportingEnabled) {
      if (leavingCashAuth) {
        claimRocketshipOwnership()
        if (matchingHandoff) {
          clearJourneyMarker()
        }
        resumed.current = true
      }
      return
    }
    if (!matchingHandoff || journeyActive) {
      return
    }
    const shouldClaimBoundary = (owner === 'negotiating' && leavingCashAuth) || owner === 'rocketship'
    const rocketshipBoundary = shouldClaimBoundary ? claimRocketshipOwnership() : undefined
    if (owner !== 'rocketship' && rocketshipBoundary === undefined) {
      return
    }
    resumed.current = true
    resumeJourney(marker.journey, marker.esid)
    const addBoundaryTags = (existingTags: readonly string[]): void => {
      for (const [key, value] of getCheckoutJourneyTags({ ...checkoutTags, existingTags })) {
        addTag(USER_JOURNEY_NAMES.CHECKOUT, `${key}:${value}`)
      }
      for (const tag of getCashAuthJourneyTags({ cashAuthJourneyId, cashAuthMethod, isAuthorized, existingTags })) {
        addTag(USER_JOURNEY_NAMES.CHECKOUT, tag)
      }
    }
    if (rocketshipBoundary?.journeyId !== undefined && rocketshipBoundary.journeyId !== marker.journey.id) {
      const replacement = getReplacedJourneyCompletion(marker.journey.created)
      if (replacement.outcome === JourneyOutcome.TERMINATED) {
        completeJourney(USER_JOURNEY_NAMES.CHECKOUT, replacement.outcome, replacement.terminationReason)
      } else {
        completeJourney(USER_JOURNEY_NAMES.CHECKOUT, replacement.outcome)
      }
      startJourney(USER_JOURNEY_NAMES.CHECKOUT, rocketshipBoundary.startedAtEpochMs, rocketshipBoundary.journeyId)
      addBoundaryTags([])
      if (isDeclined) {
        addFriction(USER_JOURNEY_NAMES.CHECKOUT, CASH_AUTH_SIGNALS.DECLINED)
      }
      if (isPollingLimitExceeded) {
        addFriction(USER_JOURNEY_NAMES.CHECKOUT, CASH_AUTH_SIGNALS.TIMEOUT)
      }
      const timeoutTerminationReason = getExpiredJourneyTimeoutTerminationReason(rocketshipBoundary.startedAtEpochMs)
      if (timeoutTerminationReason !== undefined) {
        completeJourney(USER_JOURNEY_NAMES.CHECKOUT, JourneyOutcome.TERMINATED, timeoutTerminationReason)
      }
      return
    }
    addBoundaryTags(marker.journey.tags)
    if (isDeclined && !marker.journey.frictions.includes(CASH_AUTH_SIGNALS.DECLINED)) {
      addFriction(USER_JOURNEY_NAMES.CHECKOUT, CASH_AUTH_SIGNALS.DECLINED)
    }
    if (isPollingLimitExceeded && !marker.journey.frictions.includes(CASH_AUTH_SIGNALS.TIMEOUT)) {
      addFriction(USER_JOURNEY_NAMES.CHECKOUT, CASH_AUTH_SIGNALS.TIMEOUT)
    }
    if (Date.now() - marker.journey.created > USER_JOURNEY_TIMEOUT_MS) {
      completeJourney(USER_JOURNEY_NAMES.CHECKOUT, JourneyOutcome.TERMINATED, JourneyTerminationReason.TIMEOUT_WITH_IDLE_USER)
    }
  }, [
    reportingEnabled,
    reportingEligibilityResolved,
    owner,
    journeyId,
    journeyActive,
    isAuthorized,
    isDeclined,
    hasError,
    isPollingLimitExceeded,
    checkoutTags.merchantId,
    checkoutTags.country,
    checkoutTags.cohort,
    checkoutTags.initial,
    cashAuthJourneyId,
    cashAuthMethod,
    claimRocketshipOwnership,
    resumeJourney,
    addFriction,
    addTag,
    completeJourney,
    startJourney,
  ])

  useEffect(() => {
    if (isAuthorized || isDeclined) {
      grantHandoffPending = false
    }
  }, [isAuthorized, isDeclined])

  useEffect(() => {
    if (!journeyActive || active === undefined) {
      return
    }
    for (const tag of getCashAuthJourneyTags({ cashAuthJourneyId, cashAuthMethod, isAuthorized, existingTags: active.tags })) {
      addTag(USER_JOURNEY_NAMES.CHECKOUT, tag)
    }
  }, [journeyActive, active, addTag, cashAuthJourneyId, cashAuthMethod, isAuthorized])

  useEffect(() => {
    if (isDeclined && journeyActive && active?.frictions.includes(CASH_AUTH_SIGNALS.DECLINED) === false) {
      addFriction(USER_JOURNEY_NAMES.CHECKOUT, CASH_AUTH_SIGNALS.DECLINED)
    }
  }, [isDeclined, journeyActive, active, addFriction])

  useEffect(() => {
    if (isPollingLimitExceeded && journeyActive) {
      addFriction(USER_JOURNEY_NAMES.CHECKOUT, CASH_AUTH_SIGNALS.TIMEOUT)
      completeJourney(USER_JOURNEY_NAMES.CHECKOUT, JourneyOutcome.TERMINATED, JourneyTerminationReason.TIMEOUT_WITH_IDLE_USER)
    }
  }, [isPollingLimitExceeded, journeyActive, addFriction, completeJourney])

  return reportingEligibilityResolved && ownershipExitReady
}
